import React from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, Star, PhoneIcon, MailIcon } from 'lucide-react';
const locations = ['Miami, FL', 'New York, NY', 'Houston, TX', 'Las Vegas, NV', 'Phoenix, AZ'];
interface FinalCTAProps {
  onCtaClick: () => void;
}
export function FinalCTA({
  onCtaClick
}: FinalCTAProps) {
  return <>
      {/* Final Premium CTA Section */}
      <section className="w-full py-24 lg:py-32 bg-gradient-to-br from-white via-brand-aqua/20 to-white relative overflow-hidden" data-id="element-103">
        {/* Decorative elements */}
        <div className="absolute inset-0" data-id="element-104">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-gold/10 rounded-full blur-3xl" data-id="element-105" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-brand-primary/10 rounded-full blur-3xl" data-id="element-106" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-107">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-108">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-gold/10 rounded-full mb-6" data-id="element-109">
              <Star className="w-4 h-4 text-brand-gold fill-brand-gold" data-id="element-110" />
              <span className="text-brand-gold font-semibold text-sm uppercase tracking-wider" data-id="element-111">
                Comienza Hoy
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-navy mb-8 leading-tight" data-id="element-112">
              ¿Listo para{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-primary to-brand-gold" data-id="element-113">
                Transformar Tu Cuerpo?
              </span>
            </h2>
            <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-114">
              Únete a más de 10,000 pacientes satisfechos que han logrado el
              cuerpo de sus sueños con Dermaclinic. Tu viaje hacia la confianza
              comienza aquí.
            </p>

            <motion.button onClick={onCtaClick} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.98
          }} className="inline-flex items-center gap-3 px-12 py-6 bg-gradient-to-r from-brand-primary via-brand-gold to-brand-primary text-white font-bold text-2xl rounded-full shadow-2xl shadow-brand-gold/40 transition-all duration-300 hover:shadow-brand-gold/60" aria-label="Start your transformation" data-id="element-115">
              <PhoneIcon className="w-6 h-6" data-id="element-116" />
              Comienza Tu Transformación
            </motion.button>

            <p className="mt-8 text-gray-500 flex items-center justify-center gap-6 flex-wrap" data-id="element-117">
              <span className="flex items-center gap-2" data-id="element-118">
                <Star className="w-4 h-4 text-brand-gold fill-brand-gold" data-id="element-119" />
                Consulta gratuita
              </span>
              <span data-id="element-120">•</span>
              <span className="flex items-center gap-2" data-id="element-121">
                <Star className="w-4 h-4 text-brand-gold fill-brand-gold" data-id="element-122" />
                Sin obligación
              </span>
              <span data-id="element-123">•</span>
              <span className="flex items-center gap-2" data-id="element-124">
                <Star className="w-4 h-4 text-brand-gold fill-brand-gold" data-id="element-125" />
                Financiamiento disponible
              </span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Premium Footer */}
      <footer className="w-full bg-gradient-to-br from-brand-navy via-brand-deep to-brand-navy py-16 border-t-4 border-brand-gold/30" data-id="element-126">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-127">
          {/* Top section with stats */}
          <div className="text-center mb-12 pb-12 border-b border-white/10" data-id="element-128">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-gold/20 backdrop-blur-sm rounded-full mb-6" data-id="element-129">
              <Star className="w-5 h-5 text-brand-gold fill-brand-gold" data-id="element-130" />
              <span className="text-brand-gold font-bold text-lg" data-id="element-131">
                ⭐ 10,000+ Pacientes Felices
              </span>
              <Star className="w-5 h-5 text-brand-gold fill-brand-gold" data-id="element-132" />
            </div>
            <p className="text-white/70 text-lg" data-id="element-133">
              Líderes en tratamientos estéticos avanzados en Estados Unidos
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-12" data-id="element-134">
            {/* Brand column */}
            <div data-id="element-135">
              <div className="mb-4" data-id="element-136">
                <h3 className="text-3xl font-light tracking-[0.02em] text-white" data-id="element-137">
                  DERMA
                  <span className="font-serif italic text-brand-gold ml-1" data-id="element-138">
                    clinic
                  </span>
                </h3>
              </div>
              <p className="text-white/60 leading-relaxed mb-6" data-id="element-139">
                Tratamientos estéticos premium en Estados Unidos. Transforma tu
                cuerpo con los procedimientos más avanzados disponibles.
              </p>
              <div className="flex gap-3" data-id="element-140">
                <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-brand-gold transition-colors" data-id="element-141">
                  <span className="sr-only" data-id="element-142">Facebook</span>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-143">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-144" />
                  </svg>
                </a>
                <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-brand-gold transition-colors" data-id="element-145">
                  <span className="sr-only" data-id="element-146">Instagram</span>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-147">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" data-id="element-148" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Locations column */}
            <div data-id="element-149">
              <h4 className="text-white font-bold text-lg mb-6 flex items-center gap-2" data-id="element-150">
                <MapPinIcon className="w-5 h-5 text-brand-gold" data-id="element-151" />
                Nuestras Ubicaciones
              </h4>
              <ul className="space-y-3" data-id="element-152">
                {locations.map(location => <li key={location} data-id="element-153">
                    <div className="flex items-center gap-2 text-white/60 hover:text-brand-gold transition-colors cursor-pointer" data-id="element-154">
                      <span className="w-1.5 h-1.5 bg-brand-gold rounded-full" data-id="element-155" />
                      {location}
                    </div>
                  </li>)}
              </ul>
            </div>

            {/* Contact column */}
            <div data-id="element-156">
              <h4 className="text-white font-bold text-lg mb-6" data-id="element-157">Contacto</h4>
              <ul className="space-y-4" data-id="element-158">
                <li data-id="element-159">
                  <a href="tel:+1234567890" className="flex items-center gap-3 text-white/60 hover:text-brand-gold transition-colors" data-id="element-160">
                    <PhoneIcon className="w-5 h-5" data-id="element-161" />
                    <span data-id="element-162">(555) 123-4567</span>
                  </a>
                </li>
                <li data-id="element-163">
                  <a href="mailto:info@dermaclinic.com" className="flex items-center gap-3 text-white/60 hover:text-brand-gold transition-colors" data-id="element-164">
                    <MailIcon className="w-5 h-5" data-id="element-165" />
                    <span data-id="element-166">info@dermaclinic.com</span>
                  </a>
                </li>
              </ul>

              <div className="mt-8 p-4 bg-white/5 backdrop-blur-sm rounded-2xl border border-brand-gold/20" data-id="element-167">
                <p className="text-white/80 text-sm font-medium mb-2" data-id="element-168">
                  Horario de Atención
                </p>
                <p className="text-white/60 text-sm" data-id="element-169">
                  Lun - Vie: 9:00 AM - 7:00 PM
                </p>
                <p className="text-white/60 text-sm" data-id="element-170">Sáb: 10:00 AM - 4:00 PM</p>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 border-t border-white/10" data-id="element-171">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4" data-id="element-172">
              <p className="text-white/40 text-sm" data-id="element-173">
                © 2024 Dermaclinic. Todos los derechos reservados.
              </p>
              <div className="flex gap-6" data-id="element-174">
                <a href="#" className="text-white/40 text-sm hover:text-brand-gold transition-colors" data-id="element-175">
                  Política de Privacidad
                </a>
                <a href="#" className="text-white/40 text-sm hover:text-brand-gold transition-colors" data-id="element-176">
                  Términos de Servicio
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>;
}